import MuiAlert from '@mui/material/Alert';
import Portal from '@mui/material/Portal';
import Slide from '@mui/material/Slide';
import MuiSnackbar from '@mui/material/Snackbar';
import type { SnackbarProps } from '@mui/material/Snackbar';

type CustomSnackbarProps = SnackbarProps & {
  message: string;
  variant?: 'filled' | 'outlined' | 'standard';
  severity?: 'success' | 'error' | 'warning' | 'info';
  autoHideDuration?: number | null;
  onClose?: () => void;
  isTopLevel?: boolean; // New prop to control Portal usage
};

function UpSlideTransition(props: any) {
  return <Slide {...props} direction="up" />;
}

/**
 * Snackbar component for displaying brief messages to the user.
 *
 * @param {CustomSnackbarProps} props - Props for configuring the Snackbar.
 * @param {boolean} props.open - If true, the Snackbar is visible.
 * @param {function} props.onClose - Callback fired when the Snackbar requests to be closed.
 * @param {string} props.message - The message to display in the Snackbar.
 * @param {'filled' | 'outlined' | 'standard'} [props.variant='filled'] - The variant of the Alert component.
 * @param {'success' | 'error' | 'warning' | 'info'} [props.severity='error'] - The severity level of the Alert component.
 * @param {number | null} [props.autoHideDuration=6000] - The duration in milliseconds before auto-hiding the Snackbar. Set to null to disable auto-hide.
 * @param {boolean} [props.isTopLevel=true] - If true, renders the Snackbar inside a Portal.
 * @param {object} [props.sx] - Custom styles to apply to the Snackbar component. You'll need to set position to 'absolute' to have the Snackbar appear within a relative container.
 * @param {boolean} [props.isTopLevel=true] - If true, the Snackbar is rendered within a Portal to ensure it appears above other content. This needs to be false when used inside other Portals (like dialogs) to avoid rendering issues.
 *
 * @example
 * <Snackbar
 *   open={true}
 *   onClose={() => {}}
 *   message="This is an error message"
 *   severity="error"
 * />
 *
 * @example
 * <Snackbar
 *   open={true}
 *   onClose={() => {}}
 *   message="This is a success message"
 *   severity="success"
 *   variant="outlined"
 *   autoHideDuration={3000}
 * />
 * @example
 * <Snackbar
 *   open={snackbarProps.open}
 *   onClose={handleSnackbarClose}
 *   severity={snackbarProps.severity}
 *   message={snackbarProps.message}
 *   autoHideDuration={snackbarProps.duration}
 *   sx={{ position: 'absolute' }} // <-- add this to position within relative container
 *   isTopLevel={false} // <-- add this to prevent Portal usage to allow rendering in a parent component positioned relative
 * />
 */

function StandaloneSnackbar({
  open,
  onClose,
  message,
  variant,
  severity,
  isTopLevel = true,
  autoHideDuration = 6000,
  sx,
  ...rest
}: CustomSnackbarProps) {
  const snackbarContent = (
    <MuiSnackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      TransitionComponent={UpSlideTransition}
      sx={{ '&.MuiSnackbar-root': { bottom: 70 }, ...sx }}
      {...rest}
    >
      <MuiAlert
        severity={severity || 'error'}
        variant={variant || 'filled'}
        onClose={onClose}
        elevation={8}
        sx={{ width: '100%', borderRadius: '4px' }}
      >
        {message}
      </MuiAlert>
    </MuiSnackbar>
  );

  // Conditionally wrap in Portal
  if (isTopLevel) {
    return <Portal>{snackbarContent}</Portal>;
  }
  return snackbarContent;
}

export default StandaloneSnackbar;
